/* eslint-disable no-unused-vars */
import React, { useEffect, Fragment, useState, useRef, useLayoutEffect } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { push } from "connected-react-router";
import { bool, func, arrayOf, shape } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { isIOS } from "react-device-detect";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { validateArcaCUIL, validateCUIT } from "util/form";
import { actions as notificationActions } from "reducers/notification";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as afipPaymentLinkActions, selectors as afipPaymentLinkSelectors } from "reducers/afipPaymentLink";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import ProductSelector from "pages/discounts/DiscountCreation/components/ProductSelector";
import CustomText from "pages/_components/CustomText/CustomText";
import FormattedAmount from "pages/_components/FormattedAmount";
import CustomData from "pages/_components/CustomData/CustomData";
import GridTable from "pages/_components/GridTable/GridTable";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import Checkbox from "pages/_components/Checkbox";
import Image from "pages/_components/Image";
import PaymentDueList from "../components/PaymentDueList";

const FORM_ID = "search.debt";

const DebtByCuit = (props) => {
    const formRef = useRef(null);
    const { isDesktop, fetching, dispatch, debt, cuitIsAdhered, hasSearchCuitServices, accounts, fetchingCuit } = props;
    const [itemsToDisplay, setItemsToDisplay] = useState([]);
    const [addServicesChecked, setAddServicesChecked] = useState(false);
    const [selectedServices, setSelectedServices] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [availableAccounts, setAvailableAccounts] = useState([{ id: "1" }]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedCuit, setSelectedCuit] = useState(null);
    const [disabledCuitValidation, setDisabledCuitValidation] = useState(false);

    const initialValues = {
        cuit: "",
    };

    const handleReset = () => {
        formRef.current.setFieldValue("cuit", "");
        setItemsToDisplay([]);
        setSelectedAccount(null);
        setSelectedCuit(null);
        setSelectedServices([]);
        dispatch(afipPaymentLinkActions.cleanDebt());
        dispatch(afipPaymentLinkActions.cleanHasSearchCuitFlag());
    };

    useLayoutEffect(() => {
        handleReset();
        const cuitValidation = configUtils.getBoolean("services.payment.cuit.validation.disabled", false);
        setDisabledCuitValidation(cuitValidation);
    }, []);

    useEffect(() => {
        dispatch(accountsActions.listAccounts());
    }, []);

    useEffect(() => {
        if (accounts?.length) {
            const formatOption = (account) => ({
                balance: { currency: account?.currency, quantity: account?.availableBalance },
                currency: account?.currency,
                frequentDestination: null,
                id: account?.productId,
                label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(-4)}`,
                minimumPayment: 0,
                permissions: Object.keys(account?.permissions),
                statementsLoan: null,
                totalPayment: 0,
                type: account?.accountType,
                accountNumber: account?.accountNumber,
            });

            setAvailableAccounts(accounts.filter((acc) => acc.currency === "ARS")?.map((acc) => formatOption(acc)));
        }
    }, [accounts]);

    useEffect(() => {
        if (hasSearchCuitServices && debt && debt.length > 0) {
            // ARBIBIMPLE-11056: Se genera campo id on the fly ya que el debtId se puede repetir
            //  entre servicios para diferentes CUIT/CUIL
            debt.forEach((item) => {
                item.id = `${item.codeLink}-${item.debtId}`;
            });
            setItemsToDisplay(debt);
        }
    }, [hasSearchCuitServices, debt]);

    const handleBack = () => {
        dispatch(push("/arcaPaymentSchedule"));
        handleReset();
    };

    const renderHeader = () => (
        <>
            <CustomProductHead
                titleId="afip.newPayment.title"
                backLinkTo="/arcaPaymentSchedule"
                className="accounts"
                onBack={() => handleBack()}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="afip.newPayment.title" defaultValue="Nuevo pago de servicios ARCA" />
                </div>
            </CustomProductHead>
        </>
    );

    const validation = Yup.lazy(() => {
        const validate = {
            cuit: !disabledCuitValidation
                ? Yup.string()
                      .required(i18n.get("global.error.required"))
                      .test("cuit", i18n.get("vep.cuit.invalid.error", "Ingresá un CUIT/CUIL válido"), (value) => {
                          return validateCUIT(value) || validateArcaCUIL(value);
                      })
                : Yup.string().required(i18n.get("global.error.required")),
        };
        return Yup.object().shape(validate);
    });

    const handleSubmit = (values) => {
        const cuit = values?.cuit;
        setSelectedCuit(cuit);
        dispatch(afipPaymentLinkActions.getDebtByCuit(cuit));
    };

    const handleChangeSelectedItems = (selectedItems, totalAmountSelected) => {
        if (selectedItems.length === 0) {
            setSelectedAccount(null);
        }
        setSelectedServices(selectedItems);
        setTotalAmount(totalAmountSelected);
    };

    const validAmount = (accountId) => {
        const selected = availableAccounts.find((_account) => _account.id === accountId);
        return totalAmount <= selected?.balance?.quantity;
    };

    const selectAccount = (accountId) => {
        setSelectedAccount(accountId);
        if (!validAmount(accountId)) {
            dispatch(
                notificationActions.showNotification(i18n.get("insuficient.amount.error"), "error", ["afipServices"]),
            );
        }
    };

    const continueIsNotAllowed = () => {
        const paymentIsNotAllowed =
            (itemsToDisplay.length > 0 && selectedServices.length === 0) ||
            (selectedServices.length > 0 && (!selectedAccount || !validAmount(selectedAccount)));
        if (cuitIsAdhered) {
            return paymentIsNotAllowed;
        }
        return !addServicesChecked && paymentIsNotAllowed;
    };

    const handleContinue = () => {
        if (selectedServices && selectedServices.length > 0) {
            const account = availableAccounts.filter((a) => a.id === selectedAccount)[0];
            const servicesPaymentData = { selectedServices, totalAmount, account, cuit: selectedCuit };
            dispatch(afipPaymentLinkActions.setServicesPaymentData(servicesPaymentData));
        }
        if (!cuitIsAdhered && addServicesChecked) {
            dispatch(afipPaymentLinkActions.afipServicesAddEntity(selectedCuit));
        } else {
            dispatch(push("/arcaServices/newPayment/confirm"));
        }
    };

    return (
        <Fragment>
            <Notification scopeToShow="afipServices" />
            {renderHeader()}
            <MainContainer>
                <div className="container">
                    <Formik
                        initialValues={initialValues}
                        ref={formRef}
                        validationSchema={validation}
                        onSubmit={(values, formikBag) => handleSubmit(values, formikBag)}>
                        {(allFormik) => (
                            <Form className="box box-shadow">
                                <Row style={{ display: "flex", alignContent: "space-between", padding: "10px" }}>
                                    <Col xs={12} md={4}>
                                        <Row>
                                            <Col xs={12} md={12}>
                                                <Field
                                                    component={TextField}
                                                    labelText="services.payment.cuit"
                                                    placeholderText=""
                                                    idForm={FORM_ID}
                                                    name="cuit"
                                                    type="text"
                                                    pattern="[0-9]*"
                                                    maxLength={11}
                                                    minLength={0}
                                                />
                                            </Col>
                                        </Row>
                                        {hasSearchCuitServices && !cuitIsAdhered && (
                                            <Row>
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="addService"
                                                    labelText="payments.service.schedule"
                                                    labelTextProps={{ defaultValue: "Agendar servicio" }}
                                                    onChange={(value) => setAddServicesChecked(value)}
                                                />
                                            </Row>
                                        )}
                                        {!isDesktop && (
                                            <div>
                                                <div className="py-6" />
                                                <Col className="filter-button" xs={12} md={2}>
                                                    <Button
                                                        type="submit"
                                                        className="btn-primary"
                                                        label="global.search"
                                                        loading={fetching}
                                                    />
                                                </Col>
                                            </div>
                                        )}
                                    </Col>
                                    {isDesktop && (
                                        <Col className="filter-button afip-search-button" xs={12} md={2}>
                                            <Button
                                                type="submit"
                                                className="btn-primary"
                                                label="global.search"
                                                loading={fetchingCuit}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Form>
                        )}
                    </Formik>
                    <div className="pb-6" />
                    {itemsToDisplay.length > 0 ? (
                        <Fragment>
                            <PaymentDueList
                                dataToList={itemsToDisplay}
                                {...props}
                                onChange={(selectedItems, totalAmountSelected) =>
                                    handleChangeSelectedItems(selectedItems, totalAmountSelected)
                                }
                            />
                            {!!selectedServices.length && (
                                <>
                                    <GridTable className="summary-selected-items-table mt-6">
                                        <GridTable.Container columns={3} rows={2}>
                                            <GridTable.Data columnStart={1} rowStart={1} rowWidth={isDesktop ? 2 : 1}>
                                                <div className="discount-bubble-wrapper">
                                                    <Image
                                                        src={
                                                            isIOS
                                                                ? "images/figma/icon/success-primary.svg"
                                                                : "images/figma/icon/success.svg"
                                                        }
                                                        className={classNames("svg-icon")}
                                                    />
                                                    <CustomText
                                                        text={
                                                            selectedServices.length > 1
                                                                ? `${selectedServices.length} pagos seleccionados`
                                                                : `${selectedServices.length} pago seleccionado`
                                                        }
                                                    />
                                                </div>
                                            </GridTable.Data>
                                            {!isDesktop && (
                                                <GridTable.Data
                                                    className={isIOS ? "line-dashed-wrapper" : null}
                                                    columnStart={1}
                                                    rowStart={2}
                                                    columnWidth={2}>
                                                    <div className="line-dashed" />
                                                </GridTable.Data>
                                            )}
                                            <GridTable.Data
                                                alignX="end"
                                                columnStart={2}
                                                rowStart={isDesktop ? 1 : 3}
                                                columnWidth={1}>
                                                <CustomText
                                                    className="services.payment.total.amount"
                                                    text="Monto total"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data
                                                alignX="end"
                                                columnStart={2}
                                                rowStart={isDesktop ? 2 : 4}
                                                columnWidth={1}>
                                                <FormattedAmount quantity={totalAmount} currency="ARS" />
                                            </GridTable.Data>
                                        </GridTable.Container>
                                    </GridTable>
                                </>
                            )}
                            {selectedServices && selectedServices.length > 0 && (
                                <div className="grid-table box box-shadow mt-6">
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        <div className="grid-table-data md:col-start-2 md:col-span-10">
                                            <ProductSelector
                                                name="debitAccount"
                                                idField="discount-acreditation-account"
                                                label={i18n.get("vep.payment.data.debit.account", "Cuenta débito")}
                                                placeholder={i18n.get(
                                                    "deposits.request.filters.debitAccount.placeHolder",
                                                    "Seleccioná una cuenta",
                                                )}
                                                data={{ options: availableAccounts || [] }}
                                                onChange={(value) => selectAccount(value)}
                                                value={selectedAccount}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {hasSearchCuitServices && !(cuitIsAdhered && debt.length === 0) && (
                                <div className="grid-table form-actions">
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={fetching}
                                                block
                                                onClick={() => handleContinue()}
                                                disabled={continueIsNotAllowed()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        hasSearchCuitServices && (
                            <div className="box info">
                                <CustomData
                                    icon="information_fill.svg"
                                    titleText={cuitIsAdhered ? "afip.debt.empty.1" : "afip.debt.empty.2"}
                                    titleTextType="I18N"
                                />
                            </div>
                        )
                    )}
                    <div className="pb-6" />
                </div>
            </MainContainer>
        </Fragment>
    );
};

DebtByCuit.propTypes = {
    isDesktop: bool,
    isFetchingExport: bool,
    dispatch: func.isRequired,
    isMobile: bool,
    fetching: bool.isRequired,
    fetchingCuit: bool.isRequired,
    debt: arrayOf({}).isRequired,
    cuitIsAdhered: bool.isRequired,
    hasSearchCuitServices: bool.isRequired,
    accounts: arrayOf(shape({})).isRequired,
};

DebtByCuit.defaultProps = {
    isFetchingExport: false,
    isDesktop: true,
    isMobile: false,
};

const mapStateToProps = (state) => ({
    fetching: afipPaymentLinkSelectors.getFetching(state),
    debt: afipPaymentLinkSelectors.getAfipServicesDebt(state),
    cuitIsAdhered: afipPaymentLinkSelectors.getCuitIsAdhered(state),
    hasSearchCuitServices: afipPaymentLinkSelectors.getHasSearchCuitServices(state),
    accounts: accountsSelectors.getAccounts(state),
    fetchingCuit: afipPaymentLinkSelectors.getFetchingCuit(state),
});

export default connect(mapStateToProps)(DebtByCuit);
